import React, { useEffect, useMemo, useState } from 'react';

const isFr = () => String(window.locale || 'en').toLowerCase().startsWith('fr');

/**
 * GuidedTour
 * A lightweight, reusable, page-level guided tour overlay that highlights DOM elements
 * by CSS selector and positions a tooltip nearby with step content.
 *
 * Props:
 * - isOpen: boolean
 * - steps: Array<{ id: string, title: string, description: string, targetSelector: string, position?: 'top'|'right'|'bottom'|'left', onEnter?: () => void }>
 * - onClose?: () => void
 * - onComplete?: () => void
 * - onReplayRequest?: () => void  — called when a 'replay-tour' window event fires.
 *     The parent should set its tour-open state to true, e.g. onReplayRequest={() => setTourOpen(true)}.
 * - className?: string
 *
 * Tour presence broadcasting:
 * When mounted with steps, GuidedTour sets window.__guidedTourAvailable = true and dispatches
 * a 'tour-presence' CustomEvent ({ detail: { available: true } }). On unmount it reverses this.
 * ReplayTourButton listens for this to auto-show/hide.
 */
const GuidedTour = ({ isOpen, steps = [], onClose, onComplete, onReplayRequest, className = '' }) => {
  const [current, setCurrent] = useState(0);
  const [highlightEls, setHighlightEls] = useState([]);
  const [highlightRect, setHighlightRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [tooltipMaxHeight, setTooltipMaxHeight] = useState(null);
  const isLast = current === steps.length - 1;
  const isFirst = current === 0;
  const step = steps[current];

  const computePosition = (rect, position, viewportWidth, viewportHeight) => {
    const tooltipWidth = 340;
    const tooltipHeight = 260;
    const spotlightPadding = 8; // The spotlight adds 8px padding around the element
    const gap = 16; // Gap between spotlight edge and tooltip
    const minPadding = 16;
    
    // Expand rect to account for spotlight padding
    const spotlightRect = {
      top: rect.top - spotlightPadding,
      left: rect.left - spotlightPadding,
      right: rect.right + spotlightPadding,
      bottom: rect.bottom + spotlightPadding,
      width: rect.width + spotlightPadding * 2,
      height: rect.height + spotlightPadding * 2
    };
    
    // Calculate available space outside the spotlight
    const spaceRight = viewportWidth - spotlightRect.right - gap;
    const spaceLeft = spotlightRect.left - gap;
    const spaceTop = spotlightRect.top - gap;
    const spaceBottom = viewportHeight - spotlightRect.bottom - gap;
    
    // Try positions in priority order - always position OUTSIDE the spotlight
    const tryPositions = [
      // Right of spotlight
      {
        name: 'right',
        top: Math.max(minPadding, spotlightRect.top),
        left: spotlightRect.right + gap,
        fits: spaceRight >= tooltipWidth && spotlightRect.right + gap + tooltipWidth <= viewportWidth - minPadding
      },
      // Left of spotlight  
      {
        name: 'left',
        top: Math.max(minPadding, spotlightRect.top),
        left: spotlightRect.left - tooltipWidth - gap,
        fits: spaceLeft >= tooltipWidth && spotlightRect.left - tooltipWidth - gap >= minPadding
      },
      // Above spotlight
      {
        name: 'top',
        top: spotlightRect.top - tooltipHeight - gap,
        left: Math.max(minPadding, Math.min(spotlightRect.left, viewportWidth - tooltipWidth - minPadding)),
        fits: spaceTop >= tooltipHeight && spotlightRect.top - tooltipHeight - gap >= minPadding
      },
      // Below spotlight
      {
        name: 'bottom',
        top: spotlightRect.bottom + gap,
        left: Math.max(minPadding, Math.min(spotlightRect.left, viewportWidth - tooltipWidth - minPadding)),
        fits: spaceBottom >= tooltipHeight && spotlightRect.bottom + gap + tooltipHeight <= viewportHeight - minPadding
      }
    ];
    
    // Reorder based on requested position
    const orderedPositions = [
      tryPositions.find(p => p.name === position),
      ...tryPositions.filter(p => p.name !== position)
    ].filter(Boolean);
    
    // Find first position that fits
    for (const pos of orderedPositions) {
      if (pos.fits) {
        // Clamp: ensure tooltip stays within viewport vertically
        const clampedTop = Math.min(pos.top, viewportHeight - tooltipHeight - minPadding);
        const finalTop = Math.max(minPadding, clampedTop);
        const availableHeight = viewportHeight - finalTop - minPadding;
        return { 
          top: finalTop, 
          left: pos.left, 
          maxHeight: availableHeight < tooltipHeight ? availableHeight : null 
        };
      }
    }
    
    // Fallback: position in top-right corner of viewport (never overlaps spotlight)
    // This ensures the tooltip is always visible and not overlapping
    return { 
      top: minPadding, 
      left: viewportWidth - tooltipWidth - minPadding, 
      maxHeight: Math.min(tooltipHeight, viewportHeight - minPadding * 2) 
    };
  };

  useEffect(() => {
    if (!isOpen) return;

    let rafId = null;
    let retryTimer = null;
    let mutationObserver = null;
    let scrollParents = [];

    const isScrollable = (node) => {
      if (!node || node === document.body) return false;
      const style = window.getComputedStyle(node);
      const oy = style.overflowY;
      return oy === 'auto' || oy === 'scroll';
    };

    const getScrollParents = (el) => {
      const parents = [];
      let node = el?.parentElement;
      while (node) {
        if (isScrollable(node)) parents.push(node);
        node = node.parentElement;
      }
      parents.push(window);
      return parents;
    };

    const positionNow = () => {
      if (!highlightEls || highlightEls.length === 0) return;
      // Compute union rect of all targets
      let top = Infinity, left = Infinity, right = -Infinity, bottom = -Infinity;
      highlightEls.forEach((el) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        top = Math.min(top, r.top);
        left = Math.min(left, r.left);
        right = Math.max(right, r.right);
        bottom = Math.max(bottom, r.bottom);
      });
      const rect = { top, left, right, bottom, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
      setHighlightRect(rect);
      const vpW = window.innerWidth;
      const vpH = window.innerHeight;
      const { top: posTop, left: posLeft, maxHeight } = computePosition(rect, step?.position || 'right', vpW, vpH);
      setTooltipPos({ top: posTop, left: posLeft });
      setTooltipMaxHeight(maxHeight);
    };

    const attachScrollListeners = (el) => {
      scrollParents = getScrollParents(el);
      scrollParents.forEach((p) => p.addEventListener('scroll', positionNow, { passive: true }));
    };

    const detachScrollListeners = () => {
      scrollParents.forEach((p) => p.removeEventListener('scroll', positionNow));
      scrollParents = [];
    };

    const callOnEnter = () => {
      try { step?.onEnter && step.onEnter(); } catch (_) {}
    };

    const findAndHighlight = (attempts = 30) => {
      const nodes = step ? Array.from(document.querySelectorAll(step.targetSelector)) : [];
      const visibleNodes = nodes.filter((n) => {
        try {
          const r = n.getBoundingClientRect();
          return r.width > 0 && r.height > 0;
        } catch (_) { return false; }
      });
      if (visibleNodes.length > 0) {
        setHighlightEls(visibleNodes);
        // next frame to ensure layout paint
        rafId = window.requestAnimationFrame(() => {
          positionNow();
          try { visibleNodes[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch (_) {}
          detachScrollListeners();
          visibleNodes.forEach((n) => attachScrollListeners(n));
          callOnEnter();
        });
      } else if (attempts > 0) {
        retryTimer = window.setTimeout(() => findAndHighlight(attempts - 1), 120);
      }
    };

    // Initial pass with a tiny delay to allow child components (charts) to mount
    retryTimer = window.setTimeout(() => findAndHighlight(30), 100);

    const onResize = () => positionNow();
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);

    // Watch DOM changes to re-position when tiles finish rendering
    mutationObserver = new MutationObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(positionNow);
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (retryTimer) clearTimeout(retryTimer);
      if (mutationObserver) mutationObserver.disconnect();
      detachScrollListeners();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
    };
  }, [current, step, isOpen, highlightEls]);

  useEffect(() => {
    const origOverflow = document.body.style.overflow;
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      try { document.body.classList.add('guided-tour-open'); } catch (_) {}
    }
    return () => {
      document.body.style.overflow = origOverflow;
      try { document.body.classList.remove('guided-tour-open'); } catch (_) {}
    };
  }, [isOpen]);

  // Broadcast tour presence so ReplayTourButton can auto-show/hide
  useEffect(() => {
    if (steps.length === 0) return;
    window.__guidedTourAvailable = true;
    window.dispatchEvent(new CustomEvent('tour-presence', { detail: { available: true } }));
    return () => {
      window.__guidedTourAvailable = false;
      window.dispatchEvent(new CustomEvent('tour-presence', { detail: { available: false } }));
    };
  }, [steps.length]);

  // Listen for replay-tour events (dispatched by ReplayTourButton in SupportHelpDrawer, Header, etc.)
  useEffect(() => {
    if (!onReplayRequest) return;
    const handler = () => onReplayRequest();
    window.addEventListener('replay-tour', handler);
    return () => window.removeEventListener('replay-tour', handler);
  }, [onReplayRequest]);

  if (!isOpen || steps.length === 0) return null;

  const handleDismiss = () => { setCurrent(0); setHighlightEls([]); setHighlightRect(null); onClose?.(); };

  return (
    <>
      {/* Overlay + spotlight */}
      <div className="fixed inset-0" style={{ zIndex: 3000 }}>
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={handleDismiss} />
        {highlightRect && (
          <div
            className="absolute pointer-events-none"
            style={{
              top: Math.max(0, (highlightRect.top - 8)),
              left: Math.max(0, (highlightRect.left - 8)),
              width: Math.max(0, highlightRect.width + 16),
              height: Math.max(0, highlightRect.height + 16),
              boxShadow: '0 0 0 4px rgba(var(--succession-primary-rgb, 84,137,182), 0.5), 0 0 0 9999px rgba(0,0,0,0.5)',
              borderRadius: 12,
              transition: 'all 200ms ease',
              zIndex: 3000,
            }}
          />
        )}
      </div>

      {/* Tooltip card */}
      <div
        className={`fixed rounded-xl p-6 ${className}`}
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          width: 340,
          zIndex: 3001,
          maxHeight: tooltipMaxHeight ? `${tooltipMaxHeight}px` : `calc(100vh - ${tooltipPos.top + 16}px)`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'var(--succession-bg-primary, #ffffff)',
          border: '1px solid var(--succession-border, #e5e7eb)',
          boxShadow: 'var(--succession-shadow-lg, 0 10px 15px -3px rgba(0,0,0,0.1))',
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* Header: step counter + title + close */}
        <div className="flex items-start justify-between mb-4 flex-shrink-0">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-xs font-medium px-2 py-1 rounded-full"
                style={{
                  color: 'var(--succession-primary, #5489B6)',
                  backgroundColor: 'rgba(var(--succession-primary-rgb, 84,137,182), 0.1)',
                }}
              >
                {current + 1} {isFr() ? 'de' : 'of'} {steps.length}
              </span>
            </div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--succession-text-primary, #111827)' }}>{step?.title}</h3>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 rounded-lg ml-2 flex-shrink-0 transition-colors duration-200"
            style={{ color: 'var(--succession-text-muted, #9ca3af)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--succession-bg-secondary, #f9fafb)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            aria-label="Close tutorial"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        {/* Step description */}
        <div className="mb-6 overflow-y-auto flex-1 min-h-0">
          <p className="leading-relaxed" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{step?.description}</p>
        </div>

        {/* Footer: Previous / dots / Next|Finish */}
        <div className="flex items-center justify-between flex-shrink-0">
          <button
            onClick={() => { if (!isFirst) setCurrent(c => Math.max(0, c - 1)); }}
            disabled={isFirst}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            style={{
              color: isFirst ? 'var(--succession-text-muted, #9ca3af)' : 'var(--succession-text-primary, #111827)',
              cursor: isFirst ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => { if (!isFirst) e.currentTarget.style.backgroundColor = 'var(--succession-bg-secondary, #f9fafb)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            {isFr() ? 'Retour' : 'Previous'}
          </button>

          <div className="flex gap-1">
            {steps.map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full transition-colors duration-200"
                style={{
                  backgroundColor: i === current
                    ? 'var(--succession-primary, #5489B6)'
                    : 'var(--succession-border, #e5e7eb)',
                }}
              />
            ))}
          </div>

          <button
            onClick={() => {
              if (isLast) { try { onComplete?.(); } catch (_) {} try { onClose?.(); } catch (_) {} }
              else {
                setCurrent(c => Math.min(steps.length - 1, c + 1));
                setTimeout(() => {
                  try {
                    const nodes = Array.from(document.querySelectorAll(steps[Math.min(steps.length - 1, current + 1)].targetSelector));
                    setHighlightEls(nodes);
                    positionNow();
                  } catch (_) {}
                }, 50);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-opacity duration-200"
            style={{ backgroundColor: 'var(--succession-primary, #5489B6)' }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            {isLast ? (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                {isFr() ? 'Terminer' : 'Finish'}
              </>
            ) : (
              <>
                {isFr() ? 'Suivant' : 'Next'}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default GuidedTour;


